import React, { useState, useEffect } from 'react';
import { 
  getAllServices, createService, updateService, deleteService
} from '../api/services';
import {
  getAllHRV, createHRV, updateHRV, deleteHRV,
  getAllPopularDestinations, createPopularDestination, updatePopularDestination, deletePopularDestination
} from '../api/admin';
import '../css/servicesManagement.css';

function ServicesManagement() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [loadingActions, setLoadingActions] = useState({});
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('services');
  const [hrvData, setHrvData] = useState([]);
  const [popularData, setPopularData] = useState([]);

  const [formData, setFormData] = useState({
    title: '',
    category: 'stay',
    image: '',
    description: '',
    rating: 4.0,
    reviews: 0,
    price: 0,
    minPax: 1,
    features: [],
    options: [],
    recommended: false,
    bestPrice: false,
    operationalStartTime: '',
    bookingDuration: 1,
    isActive: true
  });

  const [finishTimeHelper, setFinishTimeHelper] = useState('');

  const [hrvFormData, setHrvFormData] = useState({
    id: '',
    title: '',
    location: '',
    price: 0,
    images: [],
    bookingUrl: '',
    isActive: true
  });

  const [popularFormData, setPopularFormData] = useState({
    id: '',
    title: '',
    location: '',
    price: 0,
    url: '',
    category: 'beach',
    images: [],
    isActive: true
  });

  useEffect(() => {
    const auth = localStorage.getItem('adminAuth');
    if (auth) {
      try {
        const parsedAuth = JSON.parse(auth);
        setCurrentUser(parsedAuth);
      } catch (error) {
        console.error('Error parsing auth data:', error);
      }
    }

    fetchServices();
    fetchHRVData();
    fetchPopularData();
  }, []);

  const fetchServices = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
        setError('');
      } else {
        setLoading(true);
      }
      
      const response = await getAllServices(); // Get all services (both active and inactive)
      if (response.success) {
        setServices(response.data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      if (isRefresh) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  };

  const fetchHRVData = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
        setError('');
      }
      
      const response = await getAllHRV();
      if (response.success) {
        setHrvData(response.data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      if (isRefresh) {
        setRefreshing(false);
      }
    }
  };

  const fetchPopularData = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
        setError('');
      }
      
      const response = await getAllPopularDestinations();
      if (response.success) {
        setPopularData(response.data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      if (isRefresh) {
        setRefreshing(false);
      }
    }
  };

  const handleRefresh = () => {
    if (activeTab === 'services') {
      fetchServices(true);
    } else if (activeTab === 'hrv') {
      fetchHRVData(true);
    } else if (activeTab === 'popular') {
      fetchPopularData(true);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      category: 'stay',
      image: '',
      description: '',
      rating: 4.0,
      reviews: 0,
      price: 0,
      minPax: 1,
      features: [],
      options: [],
      recommended: false,
      bestPrice: false,
      operationalStartTime: '',
      bookingDuration: 1,
      isActive: true
    });
    setFinishTimeHelper('');
  };

  const resetHrvForm = () => {
    setHrvFormData({
      id: '',
      title: '',
      location: '',
      price: 0,
      images: [],
      bookingUrl: '',
      isActive: true
    });
  };

  const resetPopularForm = () => {
    setPopularFormData({
      id: '',
      title: '',
      location: '',
      price: 0,
      url: '',
      category: 'beach',
      images: [],
      isActive: true
    });
  };

  const handleAddService = () => {
    resetForm();
    setEditingService(null);
    setShowAddModal(true);
  };

  const handleAddHRV = () => {
    resetHrvForm();
    setEditingService(null);
    setShowAddModal(true);
  };

  const handleEditHRV = (hrv) => {
    setHrvFormData({
      id: hrv.id,
      title: hrv.title,
      location: hrv.location,
      price: hrv.price,
      images: hrv.images || [],
      bookingUrl: hrv.bookingUrl,
      isActive: hrv.isActive !== undefined ? hrv.isActive : true
    });
    setEditingService({ ...hrv, type: 'hrv' });
    setShowAddModal(true);
  };

  const handleToggleHRVStatus = async (hrv) => {
    const action = hrv.isActive ? 'deactivate' : 'activate';
    if (!window.confirm(`Are you sure you want to ${action} this HRV property?`)) {
      return;
    }

    try {
      setLoadingActions(prev => ({ ...prev, [hrv._id]: 'updating' }));

      const response = await updateHRV(hrv._id, { isActive: !hrv.isActive }, currentUser._id);
      
      if (response.success) {
        fetchHRVData(true);
        setError('');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingActions(prev => {
        const updated = { ...prev };
        delete updated[hrv._id];
        return updated;
      });
    }
  };

  const handleDeleteHRV = async (hrvId) => {
    if (!window.confirm('Are you sure you want to deactivate this HRV property?')) {
      return;
    }

    try {
      setLoadingActions(prev => ({ ...prev, [hrvId]: 'deleting' }));

      const response = await deleteHRV(hrvId, currentUser._id);
      
      if (response.success) {
        fetchHRVData(true);
        setError('');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingActions(prev => {
        const updated = { ...prev };
        delete updated[hrvId];
        return updated;
      });
    }
  };

  const handleAddPopular = () => {
    resetPopularForm();
    setEditingService(null);
    setShowAddModal(true);
  };

  const handleEditPopular = (popular) => {
    setPopularFormData({
      id: popular.id,
      title: popular.title,
      location: popular.location,
      price: popular.price,
      url: popular.url || '',
      category: popular.category || 'beach',
      images: popular.images || [],
      isActive: popular.isActive !== undefined ? popular.isActive : true
    });
    setEditingService({ ...popular, type: 'popular' });
    setShowAddModal(true);
  };

  const handleTogglePopularStatus = async (destination) => {
    const action = destination.isActive ? 'deactivate' : 'activate';
    if (!window.confirm(`Are you sure you want to ${action} this popular destination?`)) {
      return;
    }

    try {
      setLoadingActions(prev => ({ ...prev, [destination._id]: 'updating' }));

      const response = await updatePopularDestination(destination._id, { isActive: !destination.isActive }, currentUser._id);
      
      if (response.success) {
        fetchPopularData(true);
        setError('');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingActions(prev => {
        const updated = { ...prev };
        delete updated[destination._id];
        return updated;
      });
    }
  };

  const handleDeletePopular = async (popularId) => {
    if (!window.confirm('Are you sure you want to deactivate this popular destination?')) {
      return;
    }

    try {
      setLoadingActions(prev => ({ ...prev, [popularId]: 'deleting' }));

      const response = await deletePopularDestination(popularId, currentUser._id);
      
      if (response.success) {
        fetchPopularData(true);
        setError('');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingActions(prev => {
        const updated = { ...prev };
        delete updated[popularId];
        return updated;
      });
    }
  };

  const handleEditService = (service) => {
    setFormData({
      title: service.title,
      category: service.category,
      image: service.image,
      description: service.description || '',
      rating: service.rating,
      reviews: service.reviews,
      price: service.price,
      minPax: service.minPax || 1,
      features: service.features || [],
      options: service.options || [],
      recommended: service.recommended,
      bestPrice: service.bestPrice,
      operationalStartTime: service.operationalStartTime || '',
      bookingDuration: service.bookingDuration || 1,
      isActive: service.isActive
    });
    setFinishTimeHelper('');
    setEditingService(service);
    setShowAddModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentUser || !currentUser._id) {
      setError('User authentication required');
      return;
    }

    try {
      setLoadingActions(prev => ({ ...prev, submit: true }));

      const serviceData = {
        ...formData,
        features: formData.features.filter(f => f.trim() !== ''),
        options: formData.options.map(option => ({
          ...option,
          price: parseFloat(option.price) || 0,
          slots: parseInt(option.slots) || 1,
          features: (option.features || []).filter(f => f.trim() !== '')
        })).filter(option => option.name && option.name.trim() !== ''),
        price: parseFloat(formData.price),
        rating: parseFloat(formData.rating),
        reviews: parseInt(formData.reviews),
        minPax: formData.minPax ? parseInt(formData.minPax) : null
      };


      let response;
      if (editingService) {
        response = await updateService(editingService._id, serviceData, currentUser._id);
      } else {
        response = await createService(serviceData, currentUser._id);
      }

      if (response.success) {
        setShowAddModal(false);
        resetForm();
        setEditingService(null);
        fetchServices(true);
        setError('');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingActions(prev => ({ ...prev, submit: false }));
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) {
      return;
    }

    try {
      setLoadingActions(prev => ({ ...prev, [serviceId]: 'deleting' }));

      const response = await deleteService(serviceId, currentUser._id);
      
      if (response.success) {
        fetchServices(true);
        setError('');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingActions(prev => {
        const updated = { ...prev };
        delete updated[serviceId];
        return updated;
      });
    }
  };

  const calculateBookingDuration = (startTime, finishTime) => {
    if (!startTime || !finishTime) return 1;
    
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [finishHour, finishMin] = finishTime.split(':').map(Number);
    
    const startTotalMinutes = startHour * 60 + startMin;
    let finishTotalMinutes = finishHour * 60 + finishMin;
    
    // Handle cases where finish time is next day (e.g., start 22:00, finish 06:00)
    if (finishTotalMinutes <= startTotalMinutes) {
      finishTotalMinutes += 24 * 60; // Add 24 hours in minutes
    }
    
    const durationMinutes = finishTotalMinutes - startTotalMinutes;
    const durationHours = durationMinutes / 60;
    
    return Math.round(durationHours * 2) / 2; // Round to nearest 0.5 hour
  };

  const handleFinishTimeChange = (e) => {
    const finishTime = e.target.value;
    setFinishTimeHelper(finishTime);
    
    if (formData.operationalStartTime && finishTime) {
      const calculatedDuration = calculateBookingDuration(formData.operationalStartTime, finishTime);
      setFormData(prev => ({
        ...prev,
        bookingDuration: calculatedDuration
      }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // If start time changes and we have a finish time, recalculate duration
    if (name === 'operationalStartTime' && finishTimeHelper) {
      const calculatedDuration = calculateBookingDuration(value, finishTimeHelper);
      setFormData(prev => ({
        ...prev,
        bookingDuration: calculatedDuration
      }));
    }
  };

  const handleFeaturesChange = (index, value) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = value;
    setFormData(prev => ({ ...prev, features: newFeatures }));
  };

  const addFeature = () => {
    setFormData(prev => ({ ...prev, features: [...prev.features, ''] }));
  };

  const removeFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  // Service Options handlers
  const handleOptionChange = (index, field, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = { ...newOptions[index], [field]: value };
    setFormData(prev => ({ ...prev, options: newOptions }));
  };

  const handleOptionFeaturesChange = (optionIndex, featureIndex, value) => {
    const newOptions = [...formData.options];
    const newFeatures = [...(newOptions[optionIndex].features || [])];
    newFeatures[featureIndex] = value;
    newOptions[optionIndex] = { ...newOptions[optionIndex], features: newFeatures };
    setFormData(prev => ({ ...prev, options: newOptions }));
  };

  const addOptionFeature = (optionIndex) => {
    const newOptions = [...formData.options];
    if (!newOptions[optionIndex].features) {
      newOptions[optionIndex].features = [];
    }
    newOptions[optionIndex].features.push('');
    setFormData(prev => ({ ...prev, options: newOptions }));
  };

  const removeOptionFeature = (optionIndex, featureIndex) => {
    const newOptions = [...formData.options];
    newOptions[optionIndex].features = newOptions[optionIndex].features.filter((_, i) => i !== featureIndex);
    setFormData(prev => ({ ...prev, options: newOptions }));
  };

  const addOption = () => {
    if (formData.options.length >= 3) return; // Max 3 options
    setFormData(prev => ({
      ...prev,
      options: [...prev.options, { name: '', description: '', price: 0, slots: 1, features: [] }]
    }));
  };

  const removeOption = (index) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  const handleCategoryChange = (e) => {
    const newCategory = e.target.value;
    setFormData(prev => ({
      ...prev,
      category: newCategory
    }));
  };

  // HRV Form Handlers
  const handleHRVInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setHrvFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleHRVSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentUser || !currentUser._id) {
      setError('User authentication required');
      return;
    }

    try {
      setLoadingActions(prev => ({ ...prev, submit: true }));

      const hrvData = {
        ...hrvFormData,
        images: hrvFormData.images.filter(img => img.trim() !== '')
      };

      let response;
      if (editingService && editingService.type === 'hrv') {
        response = await updateHRV(editingService._id, hrvData, currentUser._id);
      } else {
        response = await createHRV(hrvData, currentUser._id);
      }

      if (response.success) {
        setShowAddModal(false);
        resetHrvForm();
        setEditingService(null);
        fetchHRVData(true);
        setError('');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingActions(prev => ({ ...prev, submit: false }));
    }
  };

  // Popular Form Handlers
  const handlePopularInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPopularFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePopularSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentUser || !currentUser._id) {
      setError('User authentication required');
      return;
    }

    try {
      setLoadingActions(prev => ({ ...prev, submit: true }));

      const destinationData = {
        ...popularFormData,
        images: popularFormData.images.filter(img => img.trim() !== '')
      };

      let response;
      if (editingService && editingService.type === 'popular') {
        response = await updatePopularDestination(editingService._id, destinationData, currentUser._id);
      } else {
        response = await createPopularDestination(destinationData, currentUser._id);
      }

      if (response.success) {
        setShowAddModal(false);
        resetPopularForm();
        setEditingService(null);
        fetchPopularData(true);
        setError('');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingActions(prev => ({ ...prev, submit: false }));
    }
  };

  // HRV Image Handlers
  const handleHRVImageChange = (index, value) => {
    const newImages = [...hrvFormData.images];
    newImages[index] = value;
    setHrvFormData(prev => ({ ...prev, images: newImages }));
  };

  const addHRVImage = () => {
    setHrvFormData(prev => ({ ...prev, images: [...prev.images, ''] }));
  };

  const removeHRVImage = (index) => {
    setHrvFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  // Popular Image Handlers
  const handlePopularImageChange = (index, value) => {
    const newImages = [...popularFormData.images];
    newImages[index] = value;
    setPopularFormData(prev => ({ ...prev, images: newImages }));
  };

  const addPopularImage = () => {
    setPopularFormData(prev => ({ ...prev, images: [...prev.images, ''] }));
  };

  const removePopularImage = (index) => {
    setPopularFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const getStatusBadge = (service) => {
    if (!service.isActive) {
      return <span className="status-badge status-inactive">Inactive</span>;
    }
    return <span className="status-badge status-active">Active</span>;
  };

  const getCategoryBadge = (category) => {
    if (!category) {
      return <span className="category-badge category-experience">Unknown</span>;
    }
    let categoryClass = 'category-experience'; // default
    if (category === 'stay') {
      categoryClass = 'category-stay';
    } else if (category === 'experience') {
      categoryClass = 'category-experience';
    } else if (category === 'event') {
      categoryClass = 'category-event';
    }
    return <span className={`category-badge ${categoryClass}`}>{category.charAt(0).toUpperCase() + category.slice(1)}</span>;
  };

  if (loading) {
    return (
      <div className="services-management">
        <div className="management-header">
          <h2>Services Management</h2>
          <p>Loading services...</p>
        </div>
      </div>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'services':
        return renderServicesContent();
      case 'hrv':
        return renderHRVContent();
      case 'popular':
        return renderPopularContent();
      default:
        return renderServicesContent();
    }
  };

  const renderServicesContent = () => (
    <div className="admin-services-list">
      {services.length === 0 ? (
        <div className="no-services">
          <i className="fas fa-concierge-bell"></i>
          <h3>No services found</h3>
          <p>Start by adding your first service</p>
        </div>
      ) : (
        services.filter(service => service && service.category).map(service => (
          <div key={service._id} className="admin-service-card">
            <div className="service-header">
              <div className="service-image">
                <img src={service.image} alt={service.title} />
              </div>
              <div className="service-info">
                <h3>{service.title}</h3>
                <div className="service-badges">
                  {getCategoryBadge(service?.category)}
                  {getStatusBadge(service)}
                  {service.recommended && <span className="badge-recommended">Recommended</span>}
                  {service.bestPrice && <span className="badge-best-price">Best Price</span>}
                </div>
              </div>
              <div className="service-price">
                <span className="price">₱{service.price}</span>
                {service.minPax && <span className="min-pax">Min {service.minPax} pax</span>}
              </div>
            </div>
            
            <div className="service-details">
              {service.description && (
                <p className="service-description">{service.description}</p>
              )}
              <div className="service-stats">
                <span><i className="fas fa-star"></i> {service.rating}</span>
                <span><i className="fas fa-comment"></i> {service.reviews} reviews</span>
                {service.operationalStartTime && (
                  <span><i className="fas fa-clock"></i> {service.operationalStartTime}</span>
                )}
                {service.bookingDuration && (
                  <span><i className="fas fa-hourglass-half"></i> {service.bookingDuration}h</span>
                )}
              </div>
              {service.features && service.features.length > 0 && (
                <div className="service-features">
                  <strong>Features:</strong>
                  <ul>
                    {service.features.map((feature, index) => (
                      <li key={index}>{feature}</li>
                    ))}
                  </ul>
                </div>
              )}
              {service.options && service.options.length > 0 && (
                <div className="service-options">
                  <strong>Options ({service.options.length}/3):</strong>
                  <div className="options-list">
                    {service.options.map((option, index) => (
                      <div key={index} className="option-item">
                        <div className="option-summary">
                          <span className="option-name">{option.name}</span>
                          <span className="option-price">₱{option.price}</span>
                          {option.slots && <span className="option-slots">{option.slots} pax</span>}
                        </div>
                        {option.description && (
                          <div className="option-description">{option.description}</div>
                        )}
                        {option.features && option.features.length > 0 && (
                          <div className="option-features">
                            {option.features.map((feature, fIndex) => (
                              <span key={fIndex} className="option-feature">{feature}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="service-actions">
              <button 
                className="btn-edit"
                onClick={() => handleEditService(service)}
                disabled={loadingActions[service._id]}
              >
                <i className="fas fa-edit"></i>
                Edit
              </button>
              <button 
                className={`btn-delete ${loadingActions[service._id] === 'deleting' ? 'loading' : ''}`}
                onClick={() => handleDeleteService(service._id)}
                disabled={loadingActions[service._id]}
              >
                {loadingActions[service._id] === 'deleting' ? (
                  <>
                    <div className="button-spinner"></div>
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-trash"></i>
                    <span>Delete</span>
                  </>
                )}
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );

  const renderHRVContent = () => (
    <div className="admin-services-list">
      {hrvData.length === 0 ? (
        <div className="no-services">
          <i className="fas fa-building"></i>
          <h3>No HRV properties found</h3>
          <p>Start by adding your first hotel, resort, or vacation house</p>
        </div>
      ) : (
        hrvData.map(hrv => (
          <div key={hrv._id} className="admin-service-card">
            <div className="service-header">
              <div className="service-image">
                {hrv.images && hrv.images.length > 0 ? (
                  <img src={hrv.images[0]} alt={hrv.title} />
                ) : (
                  <div className="no-image"><i className="fas fa-building"></i></div>
                )}
              </div>
              <div className="service-info">
                <h3>{hrv.title}</h3>
                <div className="service-badges">
                  <span className="category-badge category-stay">HRV Property</span>
                  {getStatusBadge(hrv)}
                </div>
                <p className="hrv-location"><i className="fas fa-map-marker-alt"></i> {hrv.location}</p>
                <p className="hrv-id">ID: {hrv.id}</p>
              </div>
              <div className="service-price">
                <span className="price">₱{hrv.price}</span>
              </div>
            </div>
            
            <div className="service-details">
              {hrv.images && hrv.images.length > 1 && (
                <div className="hrv-images">
                  <strong>Images ({hrv.images.length}):</strong>
                  <div className="image-gallery">
                    {hrv.images.slice(0, 3).map((img, index) => (
                      <img key={index} src={img} alt={`${hrv.title} ${index + 1}`} className="gallery-thumb" />
                    ))}
                    {hrv.images.length > 3 && (
                      <div className="more-images">+{hrv.images.length - 3} more</div>
                    )}
                  </div>
                </div>
              )}
              {hrv.bookingUrl && (
                <div className="hrv-booking">
                  <strong>Booking URL:</strong>
                  <a href={hrv.bookingUrl} target="_blank" rel="noopener noreferrer">
                    {hrv.bookingUrl.length > 50 ? hrv.bookingUrl.substring(0, 50) + '...' : hrv.bookingUrl}
                  </a>
                </div>
              )}
            </div>

            <div className="service-actions">
              <button 
                className="btn-edit"
                onClick={() => handleEditHRV(hrv)}
                disabled={loadingActions[hrv._id]}
              >
                <i className="fas fa-edit"></i>
                Edit
              </button>
              <button 
                className={`btn-toggle ${!hrv.isActive ? 'btn-activate' : 'btn-deactivate'} ${loadingActions[hrv._id] === 'updating' ? 'loading' : ''}`}
                onClick={() => handleToggleHRVStatus(hrv)}
                disabled={loadingActions[hrv._id]}
              >
                {loadingActions[hrv._id] === 'updating' ? (
                  <>
                    <div className="button-spinner"></div>
                    <span>{hrv.isActive ? 'Deactivating...' : 'Activating...'}</span>
                  </>
                ) : (
                  <>
                    <i className={`fas ${hrv.isActive ? 'fa-ban' : 'fa-check'}`}></i>
                    <span>{hrv.isActive ? 'Deactivate' : 'Activate'}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );

  const renderPopularContent = () => (
    <div className="admin-services-list">
      {popularData.length === 0 ? (
        <div className="no-services">
          <i className="fas fa-fire"></i>
          <h3>No popular destinations found</h3>
          <p>Start by adding your first popular destination</p>
        </div>
      ) : (
        popularData.map(destination => (
          <div key={destination._id} className="admin-service-card">
            <div className="service-header">
              <div className="service-image">
                {destination.images && destination.images.length > 0 ? (
                  <img src={destination.images[0]} alt={destination.title} />
                ) : (
                  <div className="no-image"><i className="fas fa-fire"></i></div>
                )}
              </div>
              <div className="service-info">
                <h3>{destination.title}</h3>
                <div className="service-badges">
                  <span className="category-badge category-experience">{destination.category ? destination.category.charAt(0).toUpperCase() + destination.category.slice(1) : 'Popular'}</span>
                  {getStatusBadge(destination)}
                </div>
                <p className="hrv-location"><i className="fas fa-map-marker-alt"></i> {destination.location}</p>
                <p className="hrv-id">ID: {destination.id}</p>
              </div>
              <div className="service-price">
                <span className="price">₱{destination.price}</span>
              </div>
            </div>
            
            <div className="service-details">
              {destination.images && destination.images.length > 1 && (
                <div className="hrv-images">
                  <strong>Images ({destination.images.length}):</strong>
                  <div className="image-gallery">
                    {destination.images.slice(0, 3).map((img, index) => (
                      <img key={index} src={img} alt={`${destination.title} ${index + 1}`} className="gallery-thumb" />
                    ))}
                    {destination.images.length > 3 && (
                      <div className="more-images">+{destination.images.length - 3} more</div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="service-actions">
              <button 
                className="btn-edit"
                onClick={() => handleEditPopular(destination)}
                disabled={loadingActions[destination._id]}
              >
                <i className="fas fa-edit"></i>
                Edit
              </button>
              <button 
                className={`btn-toggle ${!destination.isActive ? 'btn-activate' : 'btn-deactivate'} ${loadingActions[destination._id] === 'updating' ? 'loading' : ''}`}
                onClick={() => handleTogglePopularStatus(destination)}
                disabled={loadingActions[destination._id]}
              >
                {loadingActions[destination._id] === 'updating' ? (
                  <>
                    <div className="button-spinner"></div>
                    <span>{destination.isActive ? 'Deactivating...' : 'Activating...'}</span>
                  </>
                ) : (
                  <>
                    <i className={`fas ${destination.isActive ? 'fa-ban' : 'fa-check'}`}></i>
                    <span>{destination.isActive ? 'Deactivate' : 'Activate'}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );

  return (
    <div className="services-management">
      <div className="management-header">
        <div className="header-content">
          <div className="header-text">
            <h2>Services Management</h2>
            <p>Manage all services and offerings</p>
          </div>
          <div className="header-actions">
            {activeTab === 'services' && (
              <>
                <button 
                  className={`refresh-btn ${refreshing ? 'refreshing' : ''}`}
                  onClick={handleRefresh}
                  disabled={refreshing}
                  title="Refresh services"
                >
                  {refreshing ? (
                    <>
                      <div className="refresh-spinner"></div>
                      <span>Refreshing...</span>
                    </>
                  ) : (
                    <>
                      <i className="fas fa-sync-alt"></i>
                      <span>Refresh</span>
                    </>
                  )}
                </button>
                <button className="add-service-btn" onClick={handleAddService}>
                  <i className="fas fa-plus"></i>
                  Add Service
                </button>
              </>
            )}
            {activeTab === 'hrv' && (
              <>
                <button 
                  className={`refresh-btn ${refreshing ? 'refreshing' : ''}`}
                  onClick={handleRefresh}
                  disabled={refreshing}
                  title="Refresh HRV properties"
                >
                  {refreshing ? (
                    <>
                      <div className="refresh-spinner"></div>
                      <span>Refreshing...</span>
                    </>
                  ) : (
                    <>
                      <i className="fas fa-sync-alt"></i>
                      <span>Refresh</span>
                    </>
                  )}
                </button>
                <button className="add-service-btn" onClick={handleAddHRV}>
                  <i className="fas fa-plus"></i>
                  Add Property
                </button>
              </>
            )}
            {activeTab === 'popular' && (
              <>
                <button 
                  className={`refresh-btn ${refreshing ? 'refreshing' : ''}`}
                  onClick={handleRefresh}
                  disabled={refreshing}
                  title="Refresh destinations"
                >
                  {refreshing ? (
                    <>
                      <div className="refresh-spinner"></div>
                      <span>Refreshing...</span>
                    </>
                  ) : (
                    <>
                      <i className="fas fa-sync-alt"></i>
                      <span>Refresh</span>
                    </>
                  )}
                </button>
                <button className="add-service-btn" onClick={handleAddPopular}>
                  <i className="fas fa-plus"></i>
                  Add Destination
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="services-tabs">
        <button 
          className={`tab-button ${activeTab === 'services' ? 'active' : ''}`}
          onClick={() => setActiveTab('services')}
        >
          <i className="fas fa-concierge-bell"></i>
          Services
        </button>
        <button 
          className={`tab-button ${activeTab === 'hrv' ? 'active' : ''}`}
          onClick={() => setActiveTab('hrv')}
        >
          <i className="fas fa-building"></i>
          HRV
        </button>
        <button 
          className={`tab-button ${activeTab === 'popular' ? 'active' : ''}`}
          onClick={() => setActiveTab('popular')}
        >
          <i className="fas fa-fire"></i>
          Popular
        </button>
      </div>

      {error && (
        <div className="error-message">
          <i className="fas fa-exclamation-triangle"></i>
          {error}
        </div>
      )}

      <div className="tab-content">
        {renderTabContent()}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>
                {activeTab === 'services' && (editingService && editingService.type !== 'hrv' && editingService.type !== 'popular' ? 'Edit Service' : 'Add New Service')}
                {activeTab === 'hrv' && (editingService && editingService.type === 'hrv' ? 'Edit Property' : 'Add New Property')}
                {activeTab === 'popular' && (editingService && editingService.type === 'popular' ? 'Edit Destination' : 'Add New Destination')}
              </h3>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            {activeTab === 'services' && (
              <form onSubmit={handleSubmit} className="service-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="title">Service Title *</label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="category">Category *</label>
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleCategoryChange}
                    required
                  >
                    <option value="stay">Stay</option>
                    <option value="experience">Experience</option>
                    <option value="event">Event</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="image">Image URL *</label>
                <input
                  type="url"
                  id="image"
                  name="image"
                  value={formData.image}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="price">Price (₱) *</label>
                  <input
                    type="number"
                    id="price"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="minPax">Minimum Pax</label>
                  <input
                    type="number"
                    id="minPax"
                    name="minPax"
                    value={formData.minPax}
                    onChange={handleInputChange}
                    min="1"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="rating">Rating</label>
                  <input
                    type="number"
                    id="rating"
                    name="rating"
                    value={formData.rating}
                    onChange={handleInputChange}
                    min="0"
                    max="5"
                    step="0.1"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="reviews">Reviews Count</label>
                  <input
                    type="number"
                    id="reviews"
                    name="reviews"
                    value={formData.reviews}
                    onChange={handleInputChange}
                    min="0"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Features</label>
                {formData.features.map((feature, index) => (
                  <div key={index} className="feature-input">
                    <input
                      type="text"
                      value={feature}
                      onChange={(e) => handleFeaturesChange(index, e.target.value)}
                      placeholder={`Feature ${index + 1}`}
                    />
                    <button
                      type="button"
                      className="remove-feature"
                      onClick={() => removeFeature(index)}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                ))}
                <button type="button" className="add-feature" onClick={addFeature}>
                  <i className="fas fa-plus"></i>
                  Add Feature
                </button>
              </div>

              {/* Service Options */}
              <div className="form-group">
                  <label>Service Options (Max 3)</label>
                  <div className="options-container">
                    {formData.options.map((option, index) => (
                      <div key={index} className="option-card">
                        <div className="option-header">
                          <h4>Option {index + 1}</h4>
                          <button
                            type="button"
                            className="remove-option"
                            onClick={() => removeOption(index)}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                        
                        <div className="option-form">
                          <div className="form-row">
                            <div className="form-group">
                              <label>Name *</label>
                              <input
                                type="text"
                                value={option.name || ''}
                                onChange={(e) => handleOptionChange(index, 'name', e.target.value)}
                                placeholder="e.g., Small, Medium, Large"
                                required
                              />
                            </div>
                            <div className="form-group">
                              <label>Price (₱) *</label>
                              <input
                                type="number"
                                value={option.price || 0}
                                onChange={(e) => handleOptionChange(index, 'price', parseFloat(e.target.value) || 0)}
                                min="0"
                                step="0.01"
                                required
                              />
                            </div>
                          </div>

                          <div className="form-row">
                            <div className="form-group">
                              <label>Passenger Slots</label>
                              <input
                                type="number"
                                value={option.slots || 1}
                                onChange={(e) => handleOptionChange(index, 'slots', parseInt(e.target.value) || 1)}
                                min="1"
                              />
                            </div>
                          </div>

                          <div className="form-group">
                            <label>Description</label>
                            <textarea
                              value={option.description || ''}
                              onChange={(e) => handleOptionChange(index, 'description', e.target.value)}
                              rows={2}
                              placeholder="Brief description of this option"
                            />
                          </div>

                          <div className="form-group">
                            <label>Features</label>
                            {(option.features || []).map((feature, featureIndex) => (
                              <div key={featureIndex} className="feature-input">
                                <input
                                  type="text"
                                  value={feature}
                                  onChange={(e) => handleOptionFeaturesChange(index, featureIndex, e.target.value)}
                                  placeholder={`Feature ${featureIndex + 1}`}
                                />
                                <button
                                  type="button"
                                  className="remove-feature"
                                  onClick={() => removeOptionFeature(index, featureIndex)}
                                >
                                  <i className="fas fa-times"></i>
                                </button>
                              </div>
                            ))}
                            <button 
                              type="button" 
                              className="add-feature small" 
                              onClick={() => addOptionFeature(index)}
                            >
                              <i className="fas fa-plus"></i>
                              Add Feature
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {formData.options.length < 3 && (
                      <button type="button" className="add-option" onClick={addOption}>
                        <i className="fas fa-plus"></i>
                        Add Option
                      </button>
                    )}
                  </div>
                </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="operationalStartTime">Operational Start Time</label>
                  <input
                    type="time"
                    id="operationalStartTime"
                    name="operationalStartTime"
                    value={formData.operationalStartTime}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="finishTimeHelper">Operational Finish Time</label>
                  <input
                    type="time"
                    id="finishTimeHelper"
                    name="finishTimeHelper"
                    value={finishTimeHelper}
                    onChange={handleFinishTimeChange}
                  />
                  <small className="help-text">For calculating booking duration only</small>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="bookingDuration">Booking Duration (hours)</label>
                <input
                  type="number"
                  id="bookingDuration"
                  name="bookingDuration"
                  value={formData.bookingDuration}
                  onChange={handleInputChange}
                  min="0.5"
                  step="0.5"
                />
                <small className="help-text">
                  {formData.operationalStartTime && finishTimeHelper 
                    ? `Calculated: ${formData.operationalStartTime} to ${finishTimeHelper} = ${formData.bookingDuration}h`
                    : 'Enter start and finish times above to auto-calculate'
                  }
                </small>
              </div>

              <div className="form-checkboxes">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="recommended"
                    checked={formData.recommended}
                    onChange={handleInputChange}
                  />
                  <span>Recommended</span>
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="bestPrice"
                    checked={formData.bestPrice}
                    onChange={handleInputChange}
                  />
                  <span>Best Price</span>
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                  />
                  <span>Active</span>
                </label>
              </div>

              <div className="form-actions">
                <button 
                  type="button" 
                  className="btn-cancel"
                  onClick={() => setShowAddModal(false)}
                  disabled={loadingActions.submit}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className={`btn-submit ${loadingActions.submit ? 'loading' : ''}`}
                  disabled={loadingActions.submit}
                >
                  {loadingActions.submit ? (
                    <>
                      <div className="button-spinner"></div>
                      <span>{editingService ? 'Updating...' : 'Creating...'}</span>
                    </>
                  ) : (
                    <span>{editingService ? 'Update Service' : 'Create Service'}</span>
                  )}
                </button>
              </div>
            </form>
            )}

            {activeTab === 'hrv' && (
              <form onSubmit={handleHRVSubmit} className="service-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="id">Property ID *</label>
                    <input
                      type="text"
                      id="id"
                      name="id"
                      value={hrvFormData.id}
                      onChange={handleHRVInputChange}
                      required
                      placeholder="e.g., hotel-cebu-grand"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="title">Property Title *</label>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      value={hrvFormData.title}
                      onChange={handleHRVInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="location">Location *</label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    value={hrvFormData.location}
                    onChange={handleHRVInputChange}
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="price">Price (₱) *</label>
                    <input
                      type="number"
                      id="price"
                      name="price"
                      value={hrvFormData.price}
                      onChange={handleHRVInputChange}
                      min="0"
                      step="0.01"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="bookingUrl">Booking URL *</label>
                    <input
                      type="url"
                      id="bookingUrl"
                      name="bookingUrl"
                      value={hrvFormData.bookingUrl}
                      onChange={handleHRVInputChange}
                      required
                      placeholder="https://book.hiverooms.com/..."
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Images</label>
                  {hrvFormData.images.map((image, index) => (
                    <div key={index} className="feature-input">
                      <input
                        type="url"
                        value={image}
                        onChange={(e) => handleHRVImageChange(index, e.target.value)}
                        placeholder={`Image URL ${index + 1}`}
                      />
                      <button
                        type="button"
                        className="remove-feature"
                        onClick={() => removeHRVImage(index)}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  ))}
                  <button type="button" className="add-feature" onClick={addHRVImage}>
                    <i className="fas fa-plus"></i>
                    Add Image URL
                  </button>
                </div>

                <div className="form-checkboxes">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={hrvFormData.isActive}
                      onChange={handleHRVInputChange}
                    />
                    <span>Active</span>
                  </label>
                </div>

                <div className="form-actions">
                  <button 
                    type="button" 
                    className="btn-cancel"
                    onClick={() => setShowAddModal(false)}
                    disabled={loadingActions.submit}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className={`btn-submit ${loadingActions.submit ? 'loading' : ''}`}
                    disabled={loadingActions.submit}
                  >
                    {loadingActions.submit ? (
                      <>
                        <div className="button-spinner"></div>
                        <span>{editingService && editingService.type === 'hrv' ? 'Updating...' : 'Creating...'}</span>
                      </>
                    ) : (
                      <span>{editingService && editingService.type === 'hrv' ? 'Update Property' : 'Create Property'}</span>
                    )}
                  </button>
                </div>
              </form>
            )}

            {activeTab === 'popular' && (
              <form onSubmit={handlePopularSubmit} className="service-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="id">Destination ID *</label>
                    <input
                      type="text"
                      id="id"
                      name="id"
                      value={popularFormData.id}
                      onChange={handlePopularInputChange}
                      required
                      placeholder="e.g., boracay-beach"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="title">Destination Title *</label>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      value={popularFormData.title}
                      onChange={handlePopularInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="location">Location *</label>
                    <input
                      type="text"
                      id="location"
                      name="location"
                      value={popularFormData.location}
                      onChange={handlePopularInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="price">Price (₱) *</label>
                    <input
                      type="number"
                      id="price"
                      name="price"
                      value={popularFormData.price}
                      onChange={handlePopularInputChange}
                      min="0"
                      step="0.01"
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="url">Destination URL</label>
                  <input
                    type="url"
                    id="url"
                    name="url"
                    value={popularFormData.url || ''}
                    onChange={handlePopularInputChange}
                    placeholder="https://example.com/destination-page"
                  />
                  <small className="help-text">Optional: Link to the destination's booking or information page</small>
                </div>

                <div className="form-group">
                  <label htmlFor="category">Category *</label>
                  <select
                    id="category"
                    name="category"
                    value={popularFormData.category}
                    onChange={handlePopularInputChange}
                    required
                  >
                    <option value="beach">Beach</option>
                    <option value="city">City</option>
                    <option value="mountains">Mountains</option>
                    <option value="family">Family</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Images</label>
                  {popularFormData.images.map((image, index) => (
                    <div key={index} className="feature-input">
                      <input
                        type="url"
                        value={image}
                        onChange={(e) => handlePopularImageChange(index, e.target.value)}
                        placeholder={`Image URL ${index + 1}`}
                      />
                      <button
                        type="button"
                        className="remove-feature"
                        onClick={() => removePopularImage(index)}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  ))}
                  <button type="button" className="add-feature" onClick={addPopularImage}>
                    <i className="fas fa-plus"></i>
                    Add Image URL
                  </button>
                </div>

                <div className="form-checkboxes">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={popularFormData.isActive}
                      onChange={handlePopularInputChange}
                    />
                    <span>Active</span>
                  </label>
                </div>

                <div className="form-actions">
                  <button 
                    type="button" 
                    className="btn-cancel"
                    onClick={() => setShowAddModal(false)}
                    disabled={loadingActions.submit}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className={`btn-submit ${loadingActions.submit ? 'loading' : ''}`}
                    disabled={loadingActions.submit}
                  >
                    {loadingActions.submit ? (
                      <>
                        <div className="button-spinner"></div>
                        <span>{editingService && editingService.type === 'popular' ? 'Updating...' : 'Creating...'}</span>
                      </>
                    ) : (
                      <span>{editingService && editingService.type === 'popular' ? 'Update Destination' : 'Create Destination'}</span>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ServicesManagement;